import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {
    EditGenericEventFormConfirmationValidationSchema,
    EditGenericEventFormValidationSchema
} from '@/features/agenda/formSchemas'
import {genericEventFormValuesToUpdatePayload} from '@/features/agenda/utils'
import {useTranslation} from '@/translations/i18n'
import {DialogHeading, DialogStore, useDialogContext} from '@ariakit/react'
import {FormProvider, SubmitErrorHandler, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {useEditEvent} from '@/features/agenda/queries/useEditEvent'
import {toast} from '@/utilities/toast/toast'

export const AgendaEditGenericEventConfirmationDialog = ({
    confirmationDialog,
    defaultValues,
    parentOnSuccess,
    parentOnError
}: {
    confirmationDialog: DialogStore
    defaultValues: EditGenericEventFormValidationSchema
    parentOnSuccess?: () => void
    parentOnError?: (error: Error) => void
}) => {
    const {t} = useTranslation()
    const form = useForm<EditGenericEventFormConfirmationValidationSchema>({
        resolver: zodResolver(EditGenericEventFormConfirmationValidationSchema),
        defaultValues: {
            ...defaultValues,
            notificationsRecipients: defaultValues.consultants.map(c => ({id: c.id, fullName: c.fullName}))
        }
    })

    const dialog = useDialogContext()

    const title = defaultValues.note || t('agenda:appointment_type_generic_event')
    const consultantName = defaultValues.consultants?.[0]?.fullName ?? ''

    const {mutate: updateEvent, isPending: isUpdatingEvent} = useEditEvent({
        onSuccess: () => {
            dialog?.hide()
            parentOnSuccess?.()
        },
        onError: error => {
            console.error(error)
            toast.error(error.message)
            parentOnError?.(error)
        }
    })

    const handleUpdateEvent = (formValues: EditGenericEventFormConfirmationValidationSchema) => {
        const payload = genericEventFormValuesToUpdatePayload(formValues)

        updateEvent({
            urlParams: {eventId: formValues.id},
            payload
        })
    }

    const handleUpdateEventError: SubmitErrorHandler<EditGenericEventFormConfirmationValidationSchema> = errors => {
        console.error(errors, {values: form.getValues()})
    }

    const onSubmit = form.handleSubmit(handleUpdateEvent, handleUpdateEventError)

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={confirmationDialog}
                onConfirm={onSubmit}
                isConfirmPending={isUpdatingEvent}
                headerSlot={
                    <Flexbox direction="column" gap={2}>
                        <DialogHeading>{t('agenda:edit_event')}</DialogHeading>
                        <Text palette="neutral" shade="600" size="textSm">
                            {`${title}${consultantName ? ` - ${consultantName}` : ''}`}
                        </Text>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
